import { Box, ButtonBase, TextField } from '@mui/material';
import { Field, FieldProps, getIn, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloField, PaloFormGrid } from '@synergy/components-palo';
import { PhoneType } from '@synergy/models';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

import { AddressFormValues } from '../models/address-form-values';
import { Contact } from '../../../common/models/contact';
import { EmailFormValues } from '../models/email-form-values';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import { PhoneFormValues } from '../models/phone-form-values';
import { useAppSelector } from '../../../store/use-appstate-selector';

/** What the head of a member and of a supplier share. */
interface PaloAccountHeadValues {
    name: string;
    mainEmail?: string;
    mainPhone?: string;
    emails: EmailFormValues[];
    phones: PhoneFormValues[];
    website: string;
    responsibleContact?: Contact;
    uid?: string;
    registerNumber?: string;
    placeOfJurisdiction?: string;
    addresses: AddressFormValues[];
    glnNumber?: string;
    financeAccountDebitor?: string;
    financeAccountCreditor?: string;
}

export interface PaloAccountHeadProps {
    /** "Gehört zu": the member's or the supplier's own search. */
    parentField: ReactNode;
    /** Where all addresses are kept. */
    onShowAddresses: () => void;
    /** Members only: an empty handler reads as "all of them". */
    responsibleAllWhenEmpty?: boolean;
}

const HEAD_AREAS = [
    'name name parent responsible',
    'address address email email',
    'phone phone fax fax',
    'uid uid gln gln',
    'register jurisdiction debitor creditor',
    'website website . .',
];

/** The same head in three columns, for a narrow mask. */
const HEAD_NARROW_AREAS = [
    'name name parent',
    'address address responsible',
    'email email phone',
    'fax uid gln',
    'register jurisdiction debitor',
    'website website creditor',
];

const addressLine = (address?: AddressFormValues): string =>
    address
        ? [
              [address.street, address.number].filter(Boolean).join(' '),
              [address.zip, address.city].filter(Boolean).join(' '),
              address.country?.name,
          ]
              .filter(Boolean)
              .join(', ')
        : '';

/**
 * The head of a member or a supplier as a grid of four columns, the way the
 * documents have it (#2250–#2253): who it is, how to reach it and its
 * finances at a glance.
 *
 * Only the standard address stands here; all of them are on their own tab,
 * since a member like Kitzbühel has 53 and they made the page long (#2261).
 * Mail and phone are the standard ones the documents print: the head showed
 * the first further entry, and a number typed in there never reached a
 * document (#2326 R-245). The fax is the first of its kind; further entries
 * stay in their tables further down.
 */
export const PaloAccountHead = ({
    parentField,
    onShowAddresses,
    responsibleAllWhenEmpty,
}: PaloAccountHeadProps): ReactElement => {
    const intl = useIntl();
    const contacts = useAppSelector(state => state.contacts.sortedByName);
    const form = useFormikContext<PaloAccountHeadValues>();
    const { values, setFieldValue } = form;

    const errorOf = (name: string): ReactNode => {
        const error = getIn(form.errors, name);

        return getIn(form.touched, name) && 'string' === typeof error ? <FormattedMessage id={error} /> : undefined;
    };

    const text = (name: string, props: { type?: string; placeholder?: string } = {}) => (
        <TextField
            name={name}
            value={getIn(values, name) ?? ''}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            size="small"
            fullWidth
            error={!!errorOf(name)}
            {...props}
        />
    );

    /** The first entry of a kind, written in place; a new one is added when there is none. */
    const firstOf = <T extends { id: number; type: string }>(
        list: T[],
        type: string,
        key: 'email' | 'number',
        listName: 'emails' | 'phones'
    ) => {
        const index = list.findIndex(entry => entry.type === type);

        return (
            <TextField
                value={index >= 0 ? (list[index] as unknown as Record<string, string>)[key] ?? '' : ''}
                onChange={event => {
                    if (index >= 0) {
                        setFieldValue(`${listName}.${index}.${key}`, event.target.value);
                    } else if (event.target.value) {
                        setFieldValue(listName, [
                            ...list,
                            { id: new Date().getTime() * -1, type, [key]: event.target.value },
                        ]);
                    }
                }}
                size="small"
                fullWidth
                error={index >= 0 && !!errorOf(`${listName}.${index}.${key}`)}
            />
        );
    };

    const standard = values.addresses?.find(address => address.isDefaultAddress) ?? values.addresses?.[0];
    const more = Math.max(0, (values.addresses?.length ?? 0) - 1);

    return (
        <PaloFormGrid columns={4} labels="side" areas={HEAD_AREAS} narrowAreas={HEAD_NARROW_AREAS}>
            <PaloField
                area="name"
                label={
                    <>
                        <FormattedMessage id="app.label.organisationName" /> *
                    </>
                }
                name="name"
                error={errorOf('name')}
            >
                {text('name')}
            </PaloField>
            <PaloField area="parent" label={<FormattedMessage id="app.label.belongsTo" />} name="parent">
                {parentField}
            </PaloField>
            <PaloField
                area="responsible"
                label={<FormattedMessage id="app.common.responsibleContact" />}
                name="responsibleContact"
            >
                {/* Never filled in and never required. On a member an empty
                    field means every handler looks after it (GF 14.10.2026). */}
                <Field name="responsibleContact">
                    {({ field }: FieldProps<PartialContact>) => (
                        <PoolContactAutocompleteList
                            items={contacts}
                            onSelected={selected => setFieldValue(field.name, selected)}
                            label={<></>}
                            placeholder={
                                responsibleAllWhenEmpty
                                    ? intl.formatMessage({
                                          id: 'app.contactsAndOrganizations.detail.responsibleAll',
                                      })
                                    : undefined
                            }
                            required={false}
                            error={!!errorOf(field.name)}
                            {...field}
                        />
                    )}
                </Field>
            </PaloField>

            <PaloField
                area="address"
                label={
                    <>
                        <FormattedMessage id="app.contactsAndOrganizations.detail.standardAddress" /> *
                    </>
                }
                name="addresses"
                error={
                    getIn(form.touched, 'addresses') && getIn(form.errors, 'addresses') ? (
                        <FormattedMessage id="app.error.required" />
                    ) : undefined
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        minHeight: 30,
                        padding: '0 10px',
                        border: `1px solid ${tokens.color.line}`,
                        borderRadius: tokens.radius.small + 'px',
                        backgroundColor: tokens.color.surfaceAlt,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: standard ? tokens.color.ink : tokens.color.ink3,
                        }}
                    >
                        {standard ? (
                            addressLine(standard)
                        ) : (
                            <FormattedMessage id="app.contactsAndOrganizations.detail.noAddress" />
                        )}
                    </Box>
                    <ButtonBase
                        onClick={onShowAddresses}
                        sx={{
                            flexShrink: 0,
                            fontSize: '12.5px',
                            color: tokens.color.accentHi,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {more > 0 ? (
                            <FormattedMessage
                                id="app.contactsAndOrganizations.detail.moreAddresses"
                                values={{ count: more }}
                            />
                        ) : (
                            <FormattedMessage id="app.contactsAndOrganizations.detail.editAddresses" />
                        )}
                    </ButtonBase>
                </Box>
            </PaloField>
            <PaloField
                area="email"
                label={
                    <>
                        <FormattedMessage id="app.label.email" /> *
                    </>
                }
                name="mainEmail"
                error={errorOf('mainEmail')}
            >
                {text('mainEmail', { type: 'email' })}
            </PaloField>

            <PaloField
                area="phone"
                label={
                    <>
                        <FormattedMessage id="app.common.phone" /> *
                    </>
                }
                name="mainPhone"
                error={errorOf('mainPhone')}
            >
                {text('mainPhone')}
            </PaloField>
            <PaloField area="fax" label={<FormattedMessage id="app.select.option.fax" />}>
                {firstOf(values.phones ?? [], PhoneType.Fax, 'number', 'phones')}
            </PaloField>

            <PaloField area="uid" label={<FormattedMessage id="app.label.uid" />} name="uid" error={errorOf('uid')}>
                {text('uid')}
            </PaloField>
            <PaloField
                area="gln"
                label={<FormattedMessage id="app.label.gln" />}
                name="glnNumber"
                error={errorOf('glnNumber')}
            >
                {text('glnNumber')}
            </PaloField>

            <PaloField
                area="register"
                label={<FormattedMessage id="app.label.organisationRegistrationNumber" />}
                name="registerNumber"
                error={errorOf('registerNumber')}
            >
                {text('registerNumber')}
            </PaloField>
            <PaloField
                area="jurisdiction"
                label={<FormattedMessage id="app.label.jurisdiction" />}
                name="placeOfJurisdiction"
                error={errorOf('placeOfJurisdiction')}
            >
                {text('placeOfJurisdiction')}
            </PaloField>
            <PaloField
                area="debitor"
                label={<FormattedMessage id="app.contactsAndOrganizations.detail.financeAccountDebitor" />}
                name="financeAccountDebitor"
                error={errorOf('financeAccountDebitor')}
            >
                {text('financeAccountDebitor', { type: 'number' })}
            </PaloField>
            <PaloField
                area="creditor"
                label={<FormattedMessage id="app.contactsAndOrganizations.detail.financeAccountCreditor" />}
                name="financeAccountCreditor"
                error={errorOf('financeAccountCreditor')}
            >
                {text('financeAccountCreditor', { type: 'number' })}
            </PaloField>

            <PaloField
                area="website"
                label={<FormattedMessage id="app.common.website" />}
                name="website"
                error={errorOf('website')}
            >
                {text('website', {
                    placeholder: intl.formatMessage({ id: 'app.contactsAndOrganizations.detail.exampleWebsite' }),
                })}
            </PaloField>
        </PaloFormGrid>
    );
};
